// Screen router and app context. Screens mount once at boot into their <section data-screen> and render on show().
import type { ScreenId, SaveData, Controls, RunResult } from '../types';
import { loadSave, persist as persistSave } from '../save';
import { unlockAudio } from '../audio/manifest';
import type { EngineVoice } from '../audio/engineVoice';
import { isConfigured } from '../net/supabase';
import { submitRun } from '../net/board';
import { drainPending } from '../net/pending';
import { mountHangar } from './hangar';
import { mountWorkshop } from './workshop';
import { mountBriefing } from './briefing';
import { mountRun } from './run';
import { mountResults } from './results';
import { mountCredits } from './credits';
import { mountSettings } from './settings';
import { mountLeaderboard } from './leaderboard';

export interface ScreenParams { craftId?: string; controls?: Controls; result?: RunResult; audioNotice?: string | null }

/** What the screens hand each other: the craft being tuned, the plan, the last result, the audio notice. */
export interface Session { craftId: string | null; controls: Controls | null; result: RunResult | null; audioNotice: string | null }

export interface AppContext {
  build: string;
  save: SaveData;
  session: Session;
  /** Writes the save; reports storage failure to the player without throwing. */
  persist(): void;
  go(id: ScreenId, params?: ScreenParams): void;
  audio: { unlock(): void; voice: EngineVoice | null };
  reducedMotion(): boolean;
  /** Re-applies settings (reduced motion attribute, voice volume) after they change. */
  applySettings(): void;
  toast(text: string): void;
  /** Submits runs queued for the global board; a no-op when offline, disabled, unconfigured or already running. */
  syncBoard(): Promise<void>;
}

export interface ScreenHandle { show(params: ScreenParams): void; hide?(): void }
export type ScreenMount = (root: HTMLElement, ctx: AppContext) => ScreenHandle;

const MOUNTS: Record<ScreenId, ScreenMount> = {
  hangar: mountHangar, workshop: mountWorkshop, briefing: mountBriefing, run: mountRun,
  results: mountResults, credits: mountCredits, settings: mountSettings, leaderboard: mountLeaderboard,
};

export function showScreen(root: HTMLElement, id: ScreenId): void {
  for (const el of root.querySelectorAll<HTMLElement>('.screen')) el.hidden = el.dataset.screen !== id;
}

function systemReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function mountApp(root: HTMLElement, build: string): void {
  const save = loadSave();
  const handles = new Map<ScreenId, ScreenHandle>();
  let current: ScreenId | null = null;
  let toastTimer = 0;
  let syncing = false;

  const ctx: AppContext = {
    build,
    save,
    session: { craftId: null, controls: null, result: null, audioNotice: null },
    audio: { unlock: unlockAudio, voice: null },
    persist() {
      if (!persistSave(ctx.save)) ctx.toast('Progress could not be saved: browser storage is unavailable.');
    },
    go(id, params = {}) {
      if (params.craftId !== undefined) ctx.session.craftId = params.craftId;
      if (params.controls !== undefined) ctx.session.controls = params.controls;
      if (params.result !== undefined) ctx.session.result = params.result;
      if (params.audioNotice !== undefined) ctx.session.audioNotice = params.audioNotice;
      if (current) handles.get(current)?.hide?.();
      current = id;
      showScreen(root, id);
      const section = root.querySelector<HTMLElement>(`[data-screen="${id}"]`);
      handles.get(id)?.show(params);
      if (section) {
        section.scrollTop = 0;
        section.focus({ preventScroll: true });
      }
    },
    reducedMotion() {
      const pref = ctx.save.settings.reducedMotion;
      return pref === 'on' || (pref === 'system' && systemReducedMotion());
    },
    applySettings() {
      document.documentElement.dataset.motion = ctx.reducedMotion() ? 'reduce' : 'full';
      ctx.audio.voice?.setVolume(ctx.save.settings.audio ? ctx.save.settings.volume : 0);
    },
    toast(text) {
      const el = document.getElementById('toast');
      if (!el) return;
      el.textContent = text;
      el.hidden = false;
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => { el.hidden = true; }, 4000);
    },
    async syncBoard() {
      if (syncing || !isConfigured || !ctx.save.settings.online || !ctx.save.pending.length) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      syncing = true;
      try {
        // The drain works on a snapshot; the processed runs are removed from whatever the save holds by the
        // time it finishes, so a run queued or a save reset meanwhile is not clobbered.
        const snapshot = ctx.save.pending;
        const report = await drainPending(ctx.save, (r) => submitRun(r, ctx.save.settings.nickname, ctx.build));
        const done = new Set(snapshot.slice(0, report.submitted + report.dropped));
        if (done.size) {
          ctx.save.pending = ctx.save.pending.filter((r) => !done.has(r));
          ctx.persist();
        }
        if (report.submitted > 0) ctx.toast(`${report.submitted} queued run${report.submitted === 1 ? '' : 's'} submitted to the global board.`);
      } finally {
        syncing = false;
      }
    },
  };

  for (const id of Object.keys(MOUNTS) as ScreenId[]) {
    const section = root.querySelector<HTMLElement>(`[data-screen="${id}"]`);
    if (!section) continue;
    handles.set(id, MOUNTS[id](section, ctx));
  }
  ctx.applySettings();
  try {
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => ctx.applySettings());
  } catch {
    // matchMedia is optional; the attribute set above still applies
  }
  ctx.go('hangar');
  void ctx.syncBoard();
  window.addEventListener('online', () => void ctx.syncBoard());
}
